/**
 * useCommitOnClickAway — fires `onClickAway` on the first mousedown outside
 * `ref`, so inline motion-prompt editors (structured blocks, clip-wide notes)
 * save by clicking elsewhere instead of through Save / Cancel buttons.
 *
 * Clicks on UI that feeds the open editor are NOT "away":
 *  - the RichTextBox @mention dropdown (portaled to <body>), and
 *  - the CINEMATOGRAPHY PRESETS panel, which inserts into the open editor.
 */

import { useEffect, useRef, type RefObject } from 'react'

const EDITOR_COMPANION_SELECTOR =
  '[data-rich-text-dropdown], [data-testid="camera-commands-panel"]'

export function useCommitOnClickAway(
  ref: RefObject<HTMLElement>,
  onClickAway: () => void,
): void {
  // Read the latest callback without re-subscribing on every render.
  const callbackRef = useRef(onClickAway)
  callbackRef.current = onClickAway

  useEffect(() => {
    const onDocMouseDown = (ev: MouseEvent) => {
      const target = ev.target
      if (!(target instanceof Element)) return
      if (ref.current?.contains(target)) return
      if (target.closest(EDITOR_COMPANION_SELECTOR)) return
      callbackRef.current()
    }
    // Capture phase: runs before the clicked element's own handlers, so the
    // commit lands before e.g. a click that opens another block's editor.
    document.addEventListener('mousedown', onDocMouseDown, true)
    return () => document.removeEventListener('mousedown', onDocMouseDown, true)
  }, [ref])
}

/** Focus the first contenteditable inside `root` and put the caret at the end
 * of its text, so a single click on read-only text drops the user straight
 * into typing in the editor that replaced it. */
export function focusEditableAtEnd(root: HTMLElement | null): void {
  const editable = root?.querySelector<HTMLElement>('[contenteditable="true"]')
  if (!editable) return
  editable.focus()
  const selection = window.getSelection()
  if (!selection) return
  const range = document.createRange()
  range.selectNodeContents(editable)
  range.collapse(false)
  selection.removeAllRanges()
  selection.addRange(range)
}
